import { useLayoutEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { SSU_SESSION_ID, getStartFlowExperimentUserId } from 'config/storage';
import { AppRouter } from 'providers/AppRouter';
import { EventTrackingProvider } from 'providers/EventTrackingProvider';
import { FlagsProvider } from 'providers/FlagsProvider';
import { GrowthbookAppProvider } from 'providers/GrowthbookAppProvider';
import { LocalizationProvider } from 'providers/LocalizationProvider';
import { SnackbarProvider } from 'providers/SnackbarProvider';
import { ThemeProvider } from 'providers/ThemeProvider';
import { migrateLegacyConsent } from 'services/oneTrust';
import { initGrowthBook } from 'services/growthbook';
import { initI18n } from 'services/i18n';
import { getLanguage } from 'services/i18n/utils';
import { rumService } from 'services/rum';

import { client, persitenceOptions } from 'services/queryClient';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

initI18n();

// Reflect the active locale on <html lang> (index.html ships a static "en").
// Set synchronously at bootstrap so language-aware vendors that read the
// document language — notably the OneTrust banner — and assistive tech see the
// real locale before they initialize. Kept in sync on change by
// useRouterSyncLanguage (called from __root.tsx).
document.documentElement.lang = getLanguage();

const sessionId = sessionStorage.getItem(SSU_SESSION_ID) ?? uuidv4();

// eslint-disable-next-line import/no-mutable-exports -- resetGrowthBook() (test-only) reassigns this; consumers read the current value via the live ESM binding.
let growthbook = initGrowthBook({
  sessionId,
  anonymousId: getStartFlowExperimentUserId(),
});

/**
 * Replaces the module-scope GrowthBook instance with a fresh one and waits for
 * its `.init()` to resolve. Only meant for integration tests, which need each
 * `renderApp()` to get its own instance so per-test MSW flag handlers are the
 * thing that answers `.init()`'s fetch, instead of a previous test's payload.
 */
export async function resetGrowthBook() {
  growthbook = initGrowthBook({
    sessionId,
    anonymousId: getStartFlowExperimentUserId(),
  });
  return growthbook.init();
}

// Carry a returning Partner's prior consent over to OneTrust before its stub
// loads, so they are not re-prompted (no banner, no flash). Trackers (incl.
// Amplitude) init later, only once their consent category is granted — see
// useConsentedTrackers.
migrateLegacyConsent();

function App() {
  useLayoutEffect(() => {
    rumService.init(sessionId);
  }, []);

  return (
    <ThemeProvider>
      <PersistQueryClientProvider
        client={client}
        persistOptions={persitenceOptions}
      >
        <LocalizationProvider>
          <GrowthbookAppProvider growthbook={growthbook}>
            <FlagsProvider sessionId={sessionId}>
              <EventTrackingProvider userId={sessionId}>
                <SnackbarProvider>
                  <AppRouter queryClient={client} rum={rumService} />
                </SnackbarProvider>
              </EventTrackingProvider>
            </FlagsProvider>
          </GrowthbookAppProvider>
        </LocalizationProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
export { growthbook };
